import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TableHead,
  RadioGroup,
  FormControlLabel,
  Radio,
  Tooltip,
  IconButton,
  CircularProgress,
} from "@mui/material";
import withSpinner from "../../HOC/withSpinner";

import uploadImageIcon from "../../assets/icons/uploadImage.svg";
import uploadImageIconYaml from "../../assets/icons/yamluploader.svg";
import uploadImageIconText from "../../assets/icons/textuploader.svg";
import { useDispatch, useSelector } from "react-redux";
import {
  openModal,
  startSpinner,
  stopSpinner,
} from "../../store/slice/UISlice";
import { useLocation } from "react-router-dom";
import Add from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { StyledBoxForImageGallery } from "../../styled";
import { useDrawer, VisionStudioSvg } from "../../components";
import CloseIcon from "@mui/icons-material/Close";
import { theme } from "../../theme";
import { AppDispatch, RootState } from "../../store/store";
import { UPLOAD_MODAL, TYPE_UPLOAD_MODAL } from "../../constants";
import { ImageGalleryProps } from "../../types/interface";
import {
  uploadImagesDelete,
  fetchImageLabelDetails,
  ImageDetails,
  fetchImagesNew,
  uploadDatasetImages,
  uploadDatasetImagesCOCO,
  uploadDatasetImagesPASCALVOC,
} from "../../store/slice/ProjectImagesUploadedSlice";
import { toast } from "react-toastify";
import DotPulseLoading from "../../components/VisionStudioDotPulse";
import TickMark from "../../assets/img/tickMark.png";
import { fetchProjectLabels } from "../../store/slice/ProjectLabelSlice";
import { fetchProjectById } from "../../store/slice/projectHandler";
import { styled } from "@mui/system";
import { useNavigate } from "react-router-dom";
import Paper from "@mui/material/Paper";
import ResponsiveTooltip from "../../components/CustomTooltip/CustomTooltip";

export interface Project {
  id: string;
  labels: number;
  images: number;
  annotations: number;
  models: number;
  imageUrl: string;
  thubmImageUrl: string;
  name: string;
  description: string;
  taskTypeId: string;
  domainTypeId: string;
  createdDate: string; // Consider changing this to Date if you parse it
  customerId: string;
  liked: boolean;
  isImageUpload?: boolean;
  isAnnotate?: boolean;
  isvalidator?: boolean;
  isTrainer?: boolean;
  isProjectOwner?: boolean;
}

const Upload: React.FC<ImageGalleryProps> = ({
  id,
  onNavigateToAnnotate,
  isImageUploader,
  isAnnotate,
  isProjectOwner,
  taskType,
}) => {
  const { openDrawer } = useDrawer();

  const location: any = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const [currPage, setCurrPage] = useState<number>(1);
  const [isImageUpload, setIsImageUpload] = useState<boolean>(false);
  const [selectedFilter, setSelectedFilter] = useState<string>("all");
  const [isClassificationProject, setIsClassificationProject] = useState(false);
  const [hideDatasetUpload, setHideDatasetUpload] = useState(false);
  const navigate = useNavigate();
  const count = 40;

  const [project, setProject] = useState<Project>({
    id: "",
    labels: 0,
    images: 0,
    annotations: 0,
    models: 0,
    imageUrl: "",
    thubmImageUrl: "",
    name: "",
    description: "",
    taskTypeId: "",
    domainTypeId: "",
    createdDate: "",
    customerId: "",
    liked: false,
  });

  const {
    images,
    totalPagesUploaded,
    currentPageUploaded,
    totalCountUploaded,
    annotatedImageCountUploaded,
    loading,
  } = useSelector((state: RootState) => state.ProjectImagesUploaded);

  const fetchData = async (page: number, count: number, filter: string) => {
    dispatch(startSpinner("Organising dataset"));
    try {
      await dispatch(
        fetchImagesNew({
          projectId: id,
          page,
          count,
          annotation: filter,
        })
      )
        .unwrap()
        .then(async (response) => {
          setIsImageUpload(response.isUploader);
          let checked = false;
          if (
            taskType === "Binary Classification" ||
            taskType === "Multi-Class Classification" ||
            taskType === "Multi-Label Classification"
          ) {
            checked = true;
            setIsClassificationProject(true);
          } else {
            checked = false;
            setIsClassificationProject(false);
          }

          // --- REDIRECT LOGIC ---
          // If isAdmin and isUploader are both false and totalCount is 0, redirect to /project
          // Note: isAdmin is not present in response, so we check localStorage or set a default
          const isAdmin =
            localStorage.getItem("isAdmin") === "true" ? true : false;
          if (
            isAdmin === false &&
            response.isUploader === false &&
            response.totalCount === 0
          ) {
            navigate("/projects");
            return;
          }

          // If filter is annotated/unannotated and no images, decrease page or reset filter
          if (
            (filter === "annotated" || filter === "unannotated") &&
            response.imageUrlModel.length === 0
          ) {
            if (page > 1) {
              // Try previous page
              fetchData(page - 1, count, filter);
              return;
            } else {
              // On first page, reset filter to all
              setSelectedFilter("all");
              fetchData(1, count, "all");
              return;
            }
          }

          if (response.imageUrlModel.length === 0) {
            if (checked) {
              dispatch(
                openModal({
                  name: TYPE_UPLOAD_MODAL,
                  taskType: taskType,
                  isImageUploader: isImageUploader,
                  isAnnotate: isAnnotate,
                })
              );
            } else {
              dispatch(openModal({ name: UPLOAD_MODAL, taskType: taskType }));
            }
          }
        });
    } catch (error) {
      toast.error("Failed to fetch image list");
    } finally {
      dispatch(stopSpinner());
    }
  };

  const fetchLabels = async (ids: string) => {
    try {
      if (ids) {
        await dispatch(fetchProjectLabels(ids))
          .unwrap()
          .then((response) => {
            console.log(response.length);
            // Fixed: Set hideDatasetUpload based on response length
            if (response.length === 0) {
              setHideDatasetUpload(false); // Enable button when no labels
              toast.info("Add label to start annotating!");
            } else {
              setHideDatasetUpload(true); // Disable button when labels exist
            }
          });
      }
    } catch (error) {
      // toast.error("Failed to fetch label list");
      // Optional: Set default state in case of error
      setHideDatasetUpload(false); // Enable button on error (adjust as needed)
    } finally {
      // Any cleanup code if needed
    }
  };

  useEffect(() => {
    console.log("hidedataset", hideDatasetUpload);
  }, [hideDatasetUpload]);
  useEffect(() => {
    if (id) {
      fetchLabels(id);
      fetchData(currPage, count, selectedFilter);
    }
  }, [id]);

  useEffect(() => {
    if (loading) {
      dispatch(startSpinner("Organising dataset"));
    } else {
      dispatch(stopSpinner());
    }
  }, [loading]);

  useEffect(() => {
    if (location.pathname.includes("project/")) {
      if (id) {
        dispatch(fetchProjectById(id))
          .then((response) => {
            setProject(response?.payload);
          })
          .catch((error) => {
            console.error("Fetch Project Failed:", error); // Log error on failure
          });
      }
    }
  }, [location]);

  const ImageContainer = styled(Box)({
    padding: "4px",
    paddingBottom: "0",
    background: "#F1F4F9",
    borderRadius: "8px",
    position: "relative",
    "&:hover img": {
      transform: "scale(1.05)",
    },
    "&:hover button": {
      display: "block",
      opacity: 1,
    },
  });

  const HoverButton = styled(Button)({
    position: "absolute",
    cursor: "pointer",
    bottom: "5px",
    fontSize: "12px",
    color: "white",
    backgroundColor: "#757575bd",
    padding: "3px",
    margin: "0",
    width: "92%",
    textAlign: "center",
    border: "0",
    display: "none",
    "&:hover": {
      backgroundColor: "#616161bd", // Slightly darker on hover
    },
  });

  const DrawerContent = ({ imageId, image }: any) => {
    const [selectedImageDetails, setSelectedImageDetails] =
      useState<ImageDetails | null>(null);

    useEffect(() => {
      const fetchDetails = async () => {
        await dispatch(fetchImageLabelDetails(imageId.id))
          .unwrap()
          .then((response) => {
            setSelectedImageDetails(response);
          });
      };
      fetchDetails();
    }, [imageId]);

    useEffect(() => {
      const element = document.querySelector(".css-1hskriy");

      if (element instanceof HTMLElement) {
        element.style.width = `300px`;
        element.style.minWidth = `300px`;
      } else {
        console.warn(
          "Element with class '.css-1hskriy' not found or is not an HTMLElement."
        );
      }
    }, []);

    const [open, setOpen] = useState<boolean>(false);

    const handleClickOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setOpen(true);
      localStorage.setItem("secondPopupOpen", true.toString());
    };

    const handleClose = (e: React.MouseEvent | Event, confirm: boolean) => {
      e.stopPropagation();
      setOpen(false);
      localStorage.setItem("secondPopupOpen", false.toString());
    };

    const handleDelete = async () => {
      try {
        await dispatch(uploadImagesDelete(imageId?.id))
          .unwrap()
          .then((response) => {
            //take response and check if length is zero, and if, change the the filter
            fetchData(currentPageUploaded - 1, count, selectedFilter);
          });
        toast.success(`Image deleted successfully`);
      } catch (error: any) {
        toast.error(error); // Show error toast to the user
      }
    };

    // Function to format date to dd/mm/yyyy
    const formatDate = (date: string) => {
      const d = new Date(date);
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();

      return `${day}/${month}/${year}`;
    };

    const formatFileSize = (bytes: number): string => {
      const kb = bytes / 1024;
      const mb = kb / 1024;

      if (mb >= 1) {
        return `${mb.toFixed(2)} MB`;
      } else {
        return `${kb.toFixed(2)} KB`;
      }
    };

    return (
      <Box sx={{ padding: "15px" }}>
        <Dialog
          open={open}
          onClose={(e) =>
            handleClose(e as React.MouseEvent<HTMLButtonElement>, false)
          }
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this item?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              id="calcelDeletePopup"
              onClick={(e) =>
                handleClose(e as React.MouseEvent<HTMLButtonElement>, false)
              }
              color="primary"
              className="hoevr"
            >
              Cancel
            </Button>
            <Button
              id="confirmDeletePopup"
              color="primary"
              className="hoevr"
              onClick={handleDelete}
            >
              Okay
            </Button>
          </DialogActions>
        </Dialog>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            // marginTop: "50px",
            alignItems: "center",
            borderBottom: "1px solid #0000001A",
          }}
        >
          <Typography
            variant="h3"
            sx={{
              padding: "20px 0",
              coln: "#333333",
              fontSize: "14px",
            }}
          >
            Summary
          </Typography>
          <Box>
            <CloseIcon style={{ cursor: "pointer" }} className="close-icon" />
          </Box>
        </Box>
        {selectedImageDetails ? (
          <>
            <Box>
              <Box>
                <img
                  src={`${process.env.REACT_APP_SERVER_BASEURL}${image}`}
                  alt="some value"
                  style={{
                    width: "100%",
                    height: "auto",
                    objectFit: "contain",
                    // border: "4px solid  transparent",
                    // borderRadius: "16px",
                    maxHeight: "400px",
                  }}
                />
              </Box>
              <TableContainer>
                <Table aria-label="simple table">
                  <TableBody>
                    <TableRow sx={{ border: 0, padding: "10px" }}>
                      <TableCell
                        component="td"
                        sx={{
                          border: 0,
                          color: "#B3B3B3CC",
                          // width: "25%",
                          padding: "10px",
                        }}
                      >
                        Project
                      </TableCell>
                      <TableCell
                        component="td"
                        sx={{
                          border: 0,
                          padding: "10px",
                          fontWeight: theme.fontWeight.fontWeightMiniBold,
                          maxWidth: "130px",
                        }}
                      >
                        <abbr
                          title={project.name}
                          style={{
                            textDecoration: "none",
                            width: "140px",
                            overflow: "hidden",
                            display: "inline-block",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {project.name}
                        </abbr>
                      </TableCell>
                    </TableRow>
                    <TableRow sx={{ border: 0 }}>
                      <TableCell
                        component="td"
                        scope="row"
                        sx={{
                          border: 0,
                          color: "#B3B3B3CC",
                          // width: "25%",
                          padding: "10px",
                        }}
                      >
                        File
                      </TableCell>
                      <TableCell
                        component="td"
                        sx={{
                          border: 0,
                          padding: "10px",
                          fontWeight: theme.fontWeight.fontWeightMiniBold,
                          maxWidth: "130px",
                        }}
                      >
                        {selectedImageDetails.imageSize !== 0
                          ? formatFileSize(selectedImageDetails.imageSize)
                          : ""}
                      </TableCell>
                    </TableRow>

                    <TableRow sx={{ border: 0, padding: "10px" }}>
                      <TableCell
                        component="td"
                        sx={{
                          border: 0,
                          color: "#B3B3B3CC",
                          // width: "25%",
                          padding: "10px",
                        }}
                      >
                        Dimensions
                      </TableCell>
                      <TableCell
                        component="td"
                        sx={{
                          border: 0,
                          padding: "10px",
                          fontWeight: theme.fontWeight.fontWeightMiniBold,
                          maxWidth: "130px",
                        }}
                      >
                        {selectedImageDetails.imageWidth}&nbsp;X&nbsp;
                        {selectedImageDetails.imageHeight}
                      </TableCell>
                    </TableRow>
                    {imageId.annotationsCount > 0 && (
                      <TableRow sx={{ border: 0, padding: "10px" }}>
                        <TableCell
                          component="td"
                          sx={{
                            border: 0,
                            color: "#B3B3B3CC",
                            // width: "25%",
                            padding: "10px",
                          }}
                        >
                          Last Annotated
                        </TableCell>
                        <TableCell
                          component="td"
                          sx={{
                            border: 0,
                            padding: "10px",
                            fontWeight: theme.fontWeight.fontWeightMiniBold,
                            maxWidth: "130px",
                          }}
                        >
                          {formatDate(selectedImageDetails.lastAnnnotatedDate)}
                          {/* {new Intl.DateTimeFormat("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        }).format(
                          new Date(selectedImageDetails.lastAnnnotatedDate)
                        )} */}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ marginTop: "10px", textAlign: "right" }}>
                <Tooltip
                  title={
                    !isProjectOwner ? "You are not the project owner." : ""
                  }
                  arrow
                  disableHoverListener={isProjectOwner}
                >
                  <span>
                    <Button
                      disabled={!isProjectOwner}
                      id="deleteImage"
                      variant="contained"
                      color="error"
                      className="deleteButton"
                      onClick={(e) => handleClickOpen(e)}
                    >
                      Remove From Dataset
                    </Button>
                  </span>
                </Tooltip>
              </Box>
              {imageId.annotationsCount > 0 && (
                <TableContainer
                  component={Paper}
                  style={{
                    width: "280px",
                    maxWidth: "280px",
                    marginTop: "20px",
                  }}
                >
                  <Table aria-label="simple table" size="small">
                    <TableHead>
                      <TableRow sx={{ background: "#F3FBFF" }}>
                        <TableCell
                          component="th"
                          align="center"
                          sx={{ maxWidth: "190px" }}
                        >
                          <h4
                            style={{
                              fontSize: "14px",
                              fontWeight: "600",
                              margin: "0",
                            }}
                          >
                            Label
                          </h4>
                        </TableCell>
                        <TableCell
                          component="th"
                          align="center"
                          sx={{ maxWidth: "80px" }}
                        >
                          <h4
                            style={{
                              fontSize: "14px",
                              fontWeight: "600",
                              margin: "0",
                            }}
                          >
                            Count
                          </h4>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedImageDetails.labels.map((label) => {
                        return (
                          <TableRow>
                            <TableCell
                              align="center"
                              scope="row"
                              // sx={{ maxWidth: "140px" }}
                            >
                              <p
                                style={{
                                  margin: "0",
                                  width: "160px",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  fontSize: "12px",
                                }}
                              >
                                {label.labelName}
                              </p>
                            </TableCell>
                            <TableCell align="center">{label.count}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </>
        ) : (
          <DotPulseLoading />
        )}
      </Box>
    );
  };

  const goToAnnotation = (id: string) => {
    if (onNavigateToAnnotate) {
      onNavigateToAnnotate();
    }
    localStorage.setItem("imageToAnnotateId", id);
  };

  const openUploadModal = () => {
    if (!isClassificationProject) {
      dispatch(openModal({ name: UPLOAD_MODAL, taskType: taskType }));
    } else {
      dispatch(
        openModal({
          name: TYPE_UPLOAD_MODAL,
          taskType: taskType,
          isImageUploader: isImageUploader,
          isAnnotate: isAnnotate,
        })
      );
    }
  };

  // Dropdown state and handler
  const [showDropdown, setShowDropdown] = useState(false);
  const [showYoloCocoModal, setShowYoloCocoModal] = useState(false);
  const [yoloCocoType, setYoloCocoType] = useState<string>("");

  const handleDropdownSelect = (type: string) => {
    setShowDropdown(false);
    setYoloCocoType(type);
    setShowYoloCocoModal(true);
  };
  // YOLO/COCO Modal state
  const [yoloImages, setYoloImages] = useState<File[]>([]);
  const [yoloTxts, setYoloTxts] = useState<File[]>([]);
  const [yoloYaml, setYoloYaml] = useState<File | File[] | null>(null);

  const [isUploading, setIsUploading] = useState(false);

  const handleYoloImagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    const validImageTypes = [
      "image/jpeg",
      "image/png",
      "image/jpg",
      "image/webp",
    ];

    const validFiles = files.filter((file) => {
      const isValid = validImageTypes.includes(file.type);
      if (!isValid) toast.error(`Unsupported file type: ${file.name}`);
      return isValid;
    });

    setYoloImages(validFiles.length > 0 ? validFiles : []);
  };
  const handleYoloTxtsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);

    files.forEach((file) => {
      if (!file.name.toLowerCase().endsWith(".txt")) {
        toast.error(
          `Invalid file type: ${file.name}. Only .txt files are allowed.`
        );
      }
    });

    setYoloTxts(
      files.filter((file) => file.name.toLowerCase().endsWith(".txt"))
    );
  };

  const handleYoloYamlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) {
      setYoloYaml(null);
      return;
    }

    if (yoloCocoType === "YOLO") {
      const file = files[0];
      const fileName = file.name.toLowerCase();
      const isYaml = fileName.endsWith(".yaml") || fileName.endsWith(".yml");
      if (!isYaml) {
        toast.error(
          `Invalid file type: ${file.name}. Please upload a .yaml or .yml file for YOLO.`
        );
        setYoloYaml(null);
        return;
      }
      setYoloYaml(file); // Single file
    } else if (yoloCocoType === "COCO") {
      const file = files[0];
      const fileName = file.name.toLowerCase();
      const isJson = fileName.endsWith(".json");
      if (!isJson) {
        toast.error(
          `Invalid file type: ${file.name}. Please upload a .json file for COCO.`
        );
        setYoloYaml(null);
        return;
      }
      setYoloYaml(file); // Single file
    } else if (yoloCocoType === "PASCAL-VOC") {
      const xmlFiles = Array.from(files); // convert to File[]
      const invalidFiles = xmlFiles.filter(
        (file) => !file.name.toLowerCase().endsWith(".xml")
      );

      if (invalidFiles.length > 0) {
        toast.error(
          `Invalid file(s): ${invalidFiles
            .map((f) => f.name)
            .join(", ")}. Please upload only .xml files for PASCAL-VOC.`
        );
        setYoloYaml(null);
        return;
      }

      setYoloYaml(xmlFiles); // ✅ now it's File[]
    } else {
      toast.error("Unknown annotation format selected.");
      setYoloYaml(null);
    }
  };

  const handleYoloCocoModalClose = () => {
    setShowYoloCocoModal(false);
    setYoloImages([]);
    setYoloTxts([]);
    setYoloYaml(null);
  };

  const handleYoloCocoUpload = () => {
    setIsUploading(true);

    if (yoloCocoType === "COCO") {
      let annotationData: File | null = null;

      if (yoloYaml && !Array.isArray(yoloYaml)) {
        annotationData = new File([yoloYaml], "coco.json", {
          type: yoloYaml.type,
        });
      } else {
        toast.error("Invalid COCO annotation file.");
        setIsUploading(false);
        return;
      }

      const cocoPayload = {
        projectId: id,
        image: yoloImages,
        annotationData,
      };

      console.log("Uploading COCO data:", cocoPayload);

      dispatch(uploadDatasetImagesCOCO(cocoPayload))
        .unwrap()
        .then(() => {
          toast.success(`COCO files uploaded successfully!`);
          fetchData(1, count, selectedFilter); // Refresh images
          handleYoloCocoModalClose();
        })
        .catch((error) => {
          toast.error(
            `Failed to upload COCO files: ${error?.message || "Unknown error"}`
          );
        })
        .finally(() => {
          setIsUploading(false);
        });
    } else if (yoloCocoType === "PASCAL-VOC") {
      const xmlFiles = Array.isArray(yoloYaml)
        ? yoloYaml.filter(
            (file) =>
              file.type === "text/xml" ||
              file.name.toLowerCase().endsWith(".xml")
          )
        : [];

      if (xmlFiles.length === 0) {
        toast.error("No valid XML annotation files found.");
        setIsUploading(false);
        return;
      }

      const vocPayload = {
        projectId: id,
        image: yoloImages,
        annotations: xmlFiles,
      };

      console.log("Uploading PASCAL-VOC data:", vocPayload);

      dispatch(uploadDatasetImagesPASCALVOC(vocPayload))
        .unwrap()
        .then(() => {
          toast.success(`PASCAL-VOC files uploaded successfully!`);
          fetchData(1, count, selectedFilter); // Refresh images
          handleYoloCocoModalClose();
        })
        .catch((error) => {
          toast.error(
            `Failed to upload PASCAL-VOC files: ${
              error?.message || "Unknown error"
            }`
          );
        })
        .finally(() => {
          setIsUploading(false);
        });
    } else {
      // Assume YOLO
      if (!yoloYaml || Array.isArray(yoloYaml)) {
        toast.error("Invalid YOLO annotation file.");
        setIsUploading(false);
        return;
      }

      const datass = {
        projectId: id,
        image: yoloImages,
        label: yoloTxts,
        data: yoloYaml,
      };

      console.log("Uploading YOLO data:", datass);

      dispatch(uploadDatasetImages(datass))
        .unwrap()
        .then(() => {
          toast.success(`YOLO files uploaded successfully!`);
          fetchData(1, count, selectedFilter); // Refresh images
          handleYoloCocoModalClose();
        })
        .catch((error) => {
          toast.error(
            `Failed to upload YOLO files: ${error?.message || "Unknown error"}`
          );
        })
        .finally(() => {
          setIsUploading(false);
        });
    }
  };

  const handleFilterChange = (event: any) => {
    const newValue = event.target.value;
    setSelectedFilter(newValue);
    fetchData(1, count, newValue);
  };

  const handleNextPage = () => {
    if (currentPageUploaded - 1 === totalPagesUploaded) {
      return;
    }

    fetchData(currentPageUploaded, count, selectedFilter);
  };

  const handlePreviousPage = () => {
    if (currentPageUploaded <= 2) {
      return;
    }

    fetchData(currentPageUploaded - 2, count, selectedFilter);
  };

  return (
    <>
      <Box padding="20px 13px " style={{ position: "relative" }}>
        {images?.length > 0 ? (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: "20px",
                alignItems: "center",
                position: "sticky",
                top: "-70px",
                zIndex: "99",
              }}
            >
              <StyledBoxForImageGallery>
                <Typography variant="h5" gutterBottom>
                  Congratulations!
                  <span role="img" aria-label="star">
                    ⭐
                  </span>
                </Typography>
                <Typography variant="body1" color={"#0F6CBD"}>
                  Your dataset has been successfully uploaded. Next, proceed to
                  the annotation screen to label data.
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <Box sx={{ marginTop: "10px" }}>
                      <RadioGroup
                        id="filterUploadImages"
                        row
                        value={selectedFilter}
                        onChange={handleFilterChange}
                      >
                        <FormControlLabel
                          value="all"
                          control={<Radio />}
                          label={`All (${totalCountUploaded})`}
                        />

                        <FormControlLabel
                          value="annotated"
                          control={<Radio />}
                          label={`Annotated (${annotatedImageCountUploaded})`}
                          disabled={annotatedImageCountUploaded === 0}
                        />

                        <FormControlLabel
                          value="unannotated"
                          control={<Radio />}
                          label={`Unannotated (${
                            totalCountUploaded - annotatedImageCountUploaded
                          })`}
                          disabled={
                            totalCountUploaded - annotatedImageCountUploaded ===
                            0
                          }
                        />
                      </RadioGroup>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      position: "relative",
                    }}
                  >
                    <Tooltip
                      title={
                        !isImageUpload
                          ? "You are not assigned to the Upload task."
                          : ""
                      }
                      arrow
                      disableHoverListener={isImageUpload}
                    >
                      <span>
                        <Button
                          disabled={!isImageUpload}
                          id="addMoreFiles"
                          variant="contained"
                          color="primary"
                          sx={{ height: "max-content" }}
                          onClick={openUploadModal}
                          endIcon={<Add />}
                        >
                          Add More Files
                        </Button>
                      </span>
                    </Tooltip>
                    {/* Show dropdown only for object detection projects */}
                    {!isClassificationProject && (
                      <Box sx={{ ml: 1, position: "relative" }}>
                        <ResponsiveTooltip
                          title={
                            hideDatasetUpload
                              ? "Permission not allowed as label already created"
                              : "Upload datasets"
                          }
                        >
                          <Button
                            id="dropdownIcon"
                            variant="outlined"
                            sx={{
                              minWidth: 0,
                              px: 1,
                              height: "max-content",
                              background: hideDatasetUpload
                                ? "#e0e0e0"
                                : "transparent",
                              color: hideDatasetUpload ? "#9e9e9e" : "inherit",
                              borderColor: hideDatasetUpload
                                ? "#e0e0e0"
                                : "primary.main",
                              cursor: hideDatasetUpload
                                ? "not-allowed"
                                : "pointer",
                              "&:hover": {
                                backgroundColor: hideDatasetUpload
                                  ? "#e0e0e0"
                                  : "action.hover",
                                borderColor: hideDatasetUpload
                                  ? "#e0e0e0"
                                  : "primary.main",
                              },
                            }}
                            onClick={() => {
                              if (!hideDatasetUpload) {
                                setShowDropdown((prev) => !prev);
                              }
                            }}
                            disabled={hideDatasetUpload || !isImageUpload}
                          >
                            <span
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M5 8L10 13L15 8"
                                  stroke={
                                    hideDatasetUpload ? "#9e9e9e" : "#1976d2"
                                  }
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </span>
                          </Button>
                        </ResponsiveTooltip>
                        {showDropdown && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: "112%",
                              right: 0,
                              zIndex: 10,
                              minWidth: "200px",
                              background: "#fff",
                              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                              borderRadius: "8px",
                              p: 0.5,
                              display: "flex",
                              flexDirection: "column",
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                p: 0.5,
                                borderRadius: "6px",
                                cursor: "pointer",
                                "&:hover": { background: "#f0f0f0" },
                                border: "1px solid #1976d2",
                                color: "#1976d2",
                                mb: 1,
                                textAlign: "center",
                              }}
                              onClick={() => handleDropdownSelect("YOLO")}
                            >
                              Datasets for YOLO
                            </Box>
                            {/* <Box
                              sx={{
                                p: 0.5,
                                borderRadius: "6px",
                                cursor: "pointer",
                                "&:hover": { background: "#f0f0f0" },
                                border: "1px solid #1976d2",
                                color: "#1976d2",
                                textAlign: "center",
                              }}
                              onClick={() => handleDropdownSelect("COCO")}
                            >
                              Datasets for COCO
                            </Box>
                            <Box
                              sx={{
                                p: 0.5,
                                borderRadius: "6px",
                                cursor: "pointer",
                                "&:hover": { background: "#f0f0f0" },
                                border: "1px solid #1976d2",
                                color: "#1976d2",
                                textAlign: "center",
                              }}
                              onClick={() => handleDropdownSelect("PASCAL-VOC")}
                            >
                              Datasets for PASCAL-VOC
                            </Box> */}
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              </StyledBoxForImageGallery>
            </Box>
          </>
        ) : (
          ""
        )}
        <div style={{ height: "100%" }}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap", // Allow items to wrap into multiple rows
              // justifyContent: "space-between", // Distribute items evenly
              width: "100%", // Full width of the container
              margin: 0, // No margin on the container
              gap: "5px",
            }}
            className="parent-class"
          >
            {images &&
              images.map((item) => (
                <Box sx={{ flex: "0 0 10.7%" }}>
                  <Box
                    sx={{
                      padding: "4px",
                      background: "white",
                      borderRadius: "8px",
                      border: !item.isAnnotator
                        ? "2.5px dashed #1976d2"
                        : "1.5px solid #E0E0E0",
                      boxShadow: !item.isAnnotator
                        ? "0 0 8px 2px rgba(25, 118, 210, 0.15)"
                        : "0 1px 4px 0 rgba(0,0,0,0.04)",
                      transition: "border 0.2s, box-shadow 0.2s",
                      "&:hover": {
                        // border: "2px solid #1565c0",
                        // boxShadow: "0 0 12px 2px rgba(21, 101, 192, 0.18)",
                      },
                    }}
                  >
                    <ImageContainer>
                      <img
                        id="imageDetails"
                        src={`${process.env.REACT_APP_SERVER_BASEURL}${item.thumbUrl}`}
                        style={{
                          width: "100%",
                          objectFit: "cover",
                          borderRadius: "4px",
                          cursor: "pointer",
                        }}
                        alt=""
                        onClick={() => {
                          openDrawer(
                            "right",
                            <DrawerContent
                              imageId={item}
                              image={item.thumbUrl}
                              sx={{ top: "50px" }}
                            />
                          );
                        }}
                      />
                      {/* Show Annotate button for classification projects if isAnnotator is true */}
                      {isClassificationProject && item.isAnnotator && (
                        <HoverButton
                          disabled={!isAnnotate}
                          variant="contained"
                          onClick={() => {
                            // Navigate to annotation page for this image
                            if (onNavigateToAnnotate) {
                              onNavigateToAnnotate();
                            }
                            localStorage.setItem("imageToAnnotateId", item.id);
                            navigate(`/project/${id}`);
                          }}
                        >
                          Annotate
                        </HoverButton>
                      )}
                      {/* For non-classification projects, keep previous logic */}
                      {!isClassificationProject && item.isAnnotator && (
                        <HoverButton
                          disabled={!isAnnotate}
                          variant="contained"
                          onClick={() => {
                            goToAnnotation(item.id);
                          }}
                        >
                          Annotate
                        </HoverButton>
                      )}
                      {item.annotationsCount > 0 && (
                        <span
                          style={{
                            width: "12px",
                            textAlign: "center",
                            alignContent: "center",
                            height: "12px",
                            borderRadius: "50%",
                            top: "9px",
                            position: "absolute",
                            right: "4px",
                            fontSize: "8px",
                            background: "green",
                            color: "white",
                            opacity: "1",
                          }}
                        >
                          <img
                            src={TickMark}
                            alt=""
                            style={{
                              height: "6px",
                              width: "auto",
                              objectFit: "contain",
                            }}
                          />
                        </span>
                      )}
                    </ImageContainer>
                  </Box>
                </Box>
              ))}
          </Box>
        </div>
        <>
          {images.length > 0 && (
            <Box
              id="uploadPagination"
              sx={{
                position: "fixed",
                bottom: 20,
                left: "50%",
                transform: "translateX(-50%)",
                backgroundColor: "rgba(0, 0, 0, 0.7)",
                borderRadius: "20px",
                padding: "10px 20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                width: "fit-content",
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                &emsp;
                <ArrowBackIcon
                  style={{
                    paddingRight: "10px",
                    cursor: currentPageUploaded <= 2 ? "no-drop" : "pointer",
                  }}
                  onClick={handlePreviousPage}
                  className="arrow-back"
                />
                {currentPageUploaded > 0 ? currentPageUploaded - 1 : "0"} /{" "}
                {totalPagesUploaded}
                <ArrowForwardIcon
                  style={{
                    paddingLeft: "10px",
                    cursor:
                      currentPageUploaded - 1 === totalPagesUploaded
                        ? "no-drop"
                        : "pointer",
                  }}
                  onClick={handleNextPage}
                  className="arrow-forward"
                />
                &emsp;
              </Typography>
            </Box>
          )}
        </>
      </Box>
      <Dialog
        open={showYoloCocoModal}
        onClose={handleYoloCocoModalClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Upload {yoloCocoType} Dataset</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 3,
              mt: 2,
              justifyContent: "space-between",
            }}
          >
            {/* Images Section */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Box>
                <IconButton>
                  <VisionStudioSvg icon={uploadImageIcon} />
                  <input
                    id="yolo-images-upload"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleYoloImagesChange}
                    style={{ display: "none" }}
                  />
                </IconButton>
              </Box>
              <Box display="flex" width="100%" justifyContent="center">
                <Typography
                  variant="h6"
                  marginTop="0px"
                  sx={{
                    fontSize: "12px",
                    color: "#132A00",
                  }}
                >
                  Drop your image here, or
                  <Button
                    component="label"
                    sx={{
                      fontSize: "12px",
                      textTransform: "initial",
                    }}
                    onClick={(e) => e.stopPropagation()} // Prevent bubbling
                  >
                    browse
                    <input
                      id="yolo-images-upload"
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleYoloImagesChange}
                      style={{ display: "none" }}
                    />
                  </Button>
                  <br />
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    {yoloImages && <>{yoloImages.length} image(s) selected</>}
                  </div>
                </Typography>
              </Box>
            </Box>
            {/* TXT Section (only for YOLO) */}
            {yoloCocoType === "YOLO" && (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Box>
                  <IconButton>
                    <VisionStudioSvg icon={uploadImageIconText} />
                    <input
                      id="yolo-txts-upload"
                      type="file"
                      accept=".txt"
                      multiple
                      onChange={handleYoloTxtsChange}
                      style={{ display: "none" }}
                    />
                  </IconButton>
                </Box>
                <Box display="flex" width="100%" justifyContent="center">
                  <Typography
                    variant="h6"
                    marginTop="0px"
                    sx={{
                      fontSize: "12px",
                      color: "#132A00",
                    }}
                  >
                    Drop your .txt file here, or
                    <Button
                      component="label"
                      sx={{
                        fontSize: "12px",
                        textTransform: "initial",
                      }}
                      onClick={(e) => e.stopPropagation()} // Prevent bubbling
                    >
                      browse
                      <input
                        id="yolo-txts-upload"
                        type="file"
                        accept=".txt"
                        multiple
                        onChange={handleYoloTxtsChange}
                        style={{ display: "none" }}
                      />
                    </Button>
                    <br />
                    <div style={{ display: "flex", justifyContent: "center" }}>
                      {yoloTxts && <>{yoloTxts.length} txt file(s) selected</>}
                    </div>
                  </Typography>
                </Box>
              </Box>
            )}
            {/* YAML Section */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Box>
                <IconButton>
                  <VisionStudioSvg icon={uploadImageIconYaml} />
                  <input
                    id="yolo-yaml-upload"
                    type="file"
                    accept=".yaml,.yml"
                    onChange={handleYoloYamlChange}
                    style={{ display: "none" }}
                  />
                </IconButton>
              </Box>
              <Box display="flex" width="100%" justifyContent="center">
                <Typography
                  variant="h6"
                  marginTop="0px"
                  sx={{
                    fontSize: "12px",
                    color: "#132A00",
                  }}
                >
                  {yoloCocoType === "YOLO"
                    ? "Drop your .yaml file here, or"
                    : yoloCocoType === "PASCAL-VOC"
                    ? "Drop your .xml file here, or"
                    : "Drop your .json file here, or"}

                  <Button
                    component="label"
                    sx={{
                      fontSize: "12px",
                      textTransform: "initial",
                    }}
                    onClick={(e) => e.stopPropagation()} // Prevent bubbling
                  >
                    browse
                    <input
                      id="yolo-yaml-upload"
                      type="file"
                      accept={
                        yoloCocoType === "YOLO"
                          ? ".yaml,.yml"
                          : yoloCocoType === "PASCAL-VOC"
                          ? ".xml"
                          : ".json"
                      }
                      {...(yoloCocoType === "PASCAL-VOC"
                        ? { multiple: true }
                        : {})}
                      onChange={handleYoloYamlChange}
                      style={{ display: "none" }}
                    />
                  </Button>
                  <br />
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    {yoloYaml && (
                      <>
                        {yoloCocoType === "PASCAL-VOC"
                          ? Array.isArray(yoloYaml) && yoloYaml.length > 0
                            ? `${yoloYaml.length} .xml file(s) selected`
                            : null
                          : yoloYaml &&
                            !Array.isArray(yoloYaml) &&
                            yoloYaml.name
                          ? `${yoloYaml.name.substring(
                              0,
                              5
                            )}... .${yoloYaml.name
                              .split(".")
                              .pop()} file selected`
                          : null}
                      </>
                    )}
                  </div>
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleYoloCocoModalClose} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleYoloCocoUpload}
            color="primary"
            variant="contained"
            disabled={
              isUploading ||
              (yoloCocoType === "YOLO"
                ? !(yoloImages && yoloTxts && yoloYaml)
                : !(yoloImages && yoloYaml))
            }
            startIcon={
              isUploading ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            {isUploading ? "Uploading..." : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default withSpinner(Upload);
